import {inject, Injectable} from '@angular/core';
import {AuthService} from '@mydocma/auth';
import {ProjectService} from '@mydocma/project';
import {firstValueFrom} from 'rxjs';
import {
  DbExport,
  DbExportService,
  SystemReport,
  SystemReportFile,
  SystemReportService,
} from '@mydocma/app-container';
import {MmDbService} from '../mm-db/mm-db-service';
import {MmDbCollections} from '../mm-db/mm-db-schema';
import {MmProjectDbCollections} from '../mm-db/mm-project-db-schema';
import {OFFLINE_SERVICE} from '../mm-offline/interfaces/offline-service.token';


@Injectable()
export class MmSystemReportService extends SystemReportService {
  private readonly mmAuthService = inject(AuthService, {optional: true});
  private readonly projectService = inject(ProjectService, {optional: true});
  private readonly exportDbService = inject(DbExportService, {optional: true});
  private readonly mmDbService = inject(MmDbService, {optional: true});
  private readonly offlineService = inject(OFFLINE_SERVICE, {optional: true});

  async createReport(): Promise<SystemReport> {
    const report = await super.createReport();
    const files = await this.createFiles();
    const mmDbExports = await this.createMmDbExports();

    const db = [...(report.db ?? []), ...mmDbExports];
    return {
      ...report,
      files: files.length ? files : undefined,
      db: db.length ? db : undefined,
    } as SystemReport;
  }

  private async createMmDbExports(): Promise<DbExport[]> {
    if (!this.mmAuthService || !this.exportDbService || !this.mmDbService) {
      return [];
    }

    try {
      const user = await firstValueFrom(this.mmAuthService.user);
      if (!user?.id) {
        return [];
      }

      const dbExports: DbExport[] = [];

      const mmDb = await this.mmDbService.getDb();
      const storeNames = Object.values(MmDbCollections);
      if (mmDb) {
        const rootDbName = `mydocma/mm/${user.id}`;
        const rootExport = await this.exportDbService.exportDb(
          rootDbName,
          storeNames,
          mmDb,
        );
        if (rootExport.stores.length > 0) {
          dbExports.push(rootExport);
        }
      }

      const currentProject = this.projectService
        ? await firstValueFrom(this.projectService.currentProject)
        : null;
      const projectId = currentProject?.id;

      if (projectId) {
        const projectDb = await this.mmDbService.getProjectDb(projectId);
        if (projectDb) {
          const projectDbName = `mydocma/mm/${user.id}/${projectId}`;
          const projectExport = await this.exportDbService.exportDb(
            projectDbName,
            Object.values(MmProjectDbCollections),
            projectDb,
          );
          if (projectExport.stores.length > 0) {
            dbExports.push(projectExport);
          }
        }
      }

      return dbExports;
    } catch {
      return [];
    }
  }

  protected async createFiles(): Promise<SystemReportFile[]> {


    return await this.createFaultAttachmentFiles();

  }

  private async createFaultAttachmentFiles(): Promise<SystemReportFile[]> {
    if (!this.mmDbService || !this.offlineService || !this.projectService) {
      return [];
    }

    try {
      const currentProject = await firstValueFrom(this.projectService.currentProject);
      const projectId = currentProject?.id;
      if (!projectId) {
        return [];
      }

      const projectDb = await this.mmDbService.getProjectDb(projectId);
      if (!projectDb) {
        return [];
      }

      const patches = await projectDb.getAll(MmProjectDbCollections.fault_patches as never) as any[];
      if (!patches?.length) {
        return [];
      }

      const attachments = this.extractAttachmentEntries(patches);
      if (!attachments.length) {
        return [];
      }

      const files: SystemReportFile[] = [];
      for (const attachment of attachments) {
        const blob = await this.offlineService.getAttachment(attachment.attachmentId, attachment.fileName);
        if (!blob) {
          continue;
        }

        const file = new File([blob], attachment?.fileName ?? attachment.attachmentId, {
          type: blob.type || attachment.mimeType || 'application/octet-stream',
        });
        const basePath = attachment.isActivityAttachment
          ? `files/faults/${attachment.faultId}/activities`
          : `files/faults/${attachment.faultId}/attachments`;

        files.push({
          path: `${basePath}/${attachment.attachmentId}/${attachment.fileName}`,
          file,
        });
      }

      return files;
    } catch {
      return [];
    }
  }

  private extractAttachmentEntries(
    patches: any[],
  ): Array<{
    faultId: string;
    attachmentId: string;
    fileName: string;
    mimeType?: string;
    isActivityAttachment: boolean
  }> {
    const collection = new Map<
      string,
      { faultId: string; attachmentId: string; fileName: string; mimeType?: string; isActivityAttachment: boolean }
    >();

    for (const patch of patches) {
      const faultId = patch?.fUid;
      const operation = patch?.operation;
      const value = operation?.value;
      const path = (operation?.path ?? '') as string;

      const isAdd = operation?.op === 'add';
      const isFilePath = path.includes('/Files');
      const isActivityAttachment = path.includes('/Activities/Files');
      const attachmentId = value?.UniqueId;
      const fileName = value?.FileName;

      if (!isAdd || !isFilePath || !faultId || !attachmentId || !fileName) {
        continue;
      }

      const key = `${faultId}:${isActivityAttachment ? 'activities' : 'attachments'}:${attachmentId}`;
      collection.set(key, {
        faultId,
        attachmentId,
        fileName,
        mimeType: value?.MimeType,
        isActivityAttachment,
      });
    }

    return Array.from(collection.values());
  }
}
